import React, { useEffect } from 'react';
import { setStoryData } from '../../../../store/storySlice';
import { makeDropdownOptions } from '../../../../helper/helper';
import {
  IPageDataModel,
  StoryCreationMethodEnum,
  StoryLanguageTypeEnum,
  StoryTypeByAgeEnum,
} from '../../../../interfaces/book.interface';
import { DropdownArrow } from '../../../../components/Common/DropdownArrow';
import { StoryTypes, StoryWritingStyle } from '../../../../config/story.config';
import { AppDispatch } from '../../../../store/store';
import { useDispatch } from 'react-redux';
import BackButton from '../../../../components/Common/BackButton';
import Loader from '../../../../components/Common/Loader';
import { BookPagesTextValidation } from './StepStoryInformationContainer';

export interface StepStoryDetailsInformationProps {
  storyTopic: string;
  storyType: string;
  writingStyle: string;
  storyLanguage: string;
  storyCreationMethod: StoryCreationMethodEnum;
  pagesDataText: { [namePage: string]: IPageDataModel };
  onLoadPagesDataText?: () => void;
  storyconfig: Record<string, any>;
  selectedStoryConfig: string;
  setStoryTopic: (topic: string) => void;
  setStoryType: (type: string) => void;
  setWritingStyle: (style: string) => void;
  setStoryLanguage: (language: StoryLanguageTypeEnum) => void;
  setStoryCreationMethod: (method: StoryCreationMethodEnum) => void;
  setShowDescription: (description: StoryTypeByAgeEnum | boolean) => void;
  setPagesDataText: (namePage: string, text: string) => void;
  onContinue: (event: React.FormEvent<HTMLFormElement>) => void;
  onBack?: (event?: React.FormEvent<HTMLFormElement>) => void;
  submitLoader?: boolean;
  validation: BookPagesTextValidation;
  onBlurAutosave?: () => void;
}

const StepStoryDetailsInformation = ({
  setStoryLanguage,
  setStoryTopic,
  setStoryType,
  storyLanguage,
  storyTopic,
  storyType,
  writingStyle,
  storyCreationMethod,
  setWritingStyle,
  setStoryCreationMethod,
  setShowDescription,
  onContinue,
  onBack,
  pagesDataText = {},
  onLoadPagesDataText,
  setPagesDataText,
  storyconfig,
  selectedStoryConfig,
  submitLoader,
  validation,
  onBlurAutosave,
}: StepStoryDetailsInformationProps) => {
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    onLoadPagesDataText && onLoadPagesDataText();
  }, []);

  const validateAIWrittenForm = () => {
    // For AI-assisted form, we only need to check if the story topic is provided
    return (
      storyTopic.trim() !== '' && storyType.trim() !== '' && writingStyle.trim() !== '' && storyLanguage.trim() !== ''
    );
  };

  // Validation function for self-written form
  const validateSelfWrittenForm = () => {
    if (storyCreationMethod !== StoryCreationMethodEnum.SELF_WRITTEN) {
      return true; // No validation needed for AI-assisted form
    }

    const currentStoryConfig = storyconfig[selectedStoryConfig];
    if (!currentStoryConfig) {
      return true; // Config not loaded yet — skip validation until it arrives
    }

    const maxWordsPerPage = parseInt(currentStoryConfig.words_total);
    const maxTextLength = parseInt(currentStoryConfig.max_text_length);

    // Check if all pages meet the requirements
    return Object.keys(pagesDataText).every((pageName) => {
      const pageText = pagesDataText[pageName]?.text || '';
      const wordCount = pageText
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length;
      return pageText?.length > 0 && wordCount > 0 && wordCount <= maxWordsPerPage && pageText.length <= maxTextLength;
    });
  };

  // Handle continue with validation
  const handleContinue = (event: React.FormEvent<HTMLFormElement>) => {
    if (storyCreationMethod === StoryCreationMethodEnum.SELF_WRITTEN && !validateSelfWrittenForm()) {
      event.preventDefault();
      // You could add a toast notification here to inform the user about validation errors
      return;
    }
    onContinue(event);
  };

  const tabs = [
    {
      title: 'I Want to Write It Myself',
      description: 'Already know what happens? Start writing your version.',
      icon: (
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      value: StoryCreationMethodEnum.SELF_WRITTEN,
    },
    {
      title: 'Help Me Write My Story',
      description: `Start with your idea and we'll bring it to life as a real storybook.`,
      icon: (
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
          />
        </svg>
      ),
      value: StoryCreationMethodEnum.AI_ASSISTED,
    },
  ];

  // AI Assisted Story Creation Form
  const renderAIAssistedForm = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      <div className="col-span-1 sm:col-span-2 md:col-span-3">
        <label className="text-left block text-sm font-medium leading-6 text-gray-900">Story Setting &amp; Goal:</label>
        <textarea
          className="w-full rounded-md text-sm md:text-xl p-2"
          value={storyTopic}
          rows={5}
          onChange={(e) => {
            setStoryTopic(e.target.value);
            dispatch(setStoryData({ storyTopic: e.target.value }));
          }}
          onBlur={onBlurAutosave}
          placeholder="ex: An abandoned research station hidden in the Arctic, where a kid discovers old technology and must uncover what happened to the missing scientists."
        />
      </div>
      <div className="">
        <label className="text-left block text-sm font-medium leading-6 text-secondaryStoryBlackText">
          Story Type:
        </label>
        <div className="grid">
          <select
            className="row-start-1 col-start-1 w-full rounded-md text-sm md:text-xl p-2 appearance-none"
            value={storyType}
            onChange={(e) => {
              setStoryType(e.target.value);
              setShowDescription(StoryTypeByAgeEnum.STORY);
              dispatch(setStoryData({ storyType: e.target.value }));
            }}
            onBlur={onBlurAutosave}
          >
            <option value="" disabled>
              Please Select Story Type
            </option>
            {makeDropdownOptions(StoryTypes())}
          </select>
          {DropdownArrow()}
        </div>
      </div>
      <div className="">
        <label className="text-left block text-sm font-medium leading-6 text-secondaryStoryBlackText">
          Writing Style:
        </label>
        <div className="grid">
          <select
            className="row-start-1 col-start-1 w-full rounded-md text-sm md:text-xl p-2 appearance-none"
            value={writingStyle}
            onChange={(e) => {
              setWritingStyle(e.target.value);
              setShowDescription(StoryTypeByAgeEnum.WRITING_STYLE);
              dispatch(setStoryData({ writingStyle: e.target.value }));
            }}
            onBlur={onBlurAutosave}
          >
            <option value="" disabled>
              Please Select Writing Style
            </option>
            {makeDropdownOptions(StoryWritingStyle())}
          </select>
          {DropdownArrow()}
        </div>
      </div>
      <div className="">
        <label className="text-left block text-sm font-medium leading-6 text-secondaryStoryBlackText">
          Write Story In:
        </label>
        <div className="grid">
          <select
            className="row-start-1 col-start-1 w-full rounded-md text-sm md:text-xl p-2 appearance-none"
            value={storyLanguage}
            onChange={(e) => {
              setStoryLanguage(e.target.value as StoryLanguageTypeEnum);
              dispatch(setStoryData({ storyLanguage: e.target.value as StoryLanguageTypeEnum }));
            }}
            onBlur={onBlurAutosave}
          >
            <option value="" disabled>
              Please Select a language
            </option>
            <option value={StoryLanguageTypeEnum.ENGLISH}>English (US)</option>
            <option value={StoryLanguageTypeEnum.UK_ENGLISH}>English (UK)</option>
            <option value={StoryLanguageTypeEnum.SPANISH}>Spanish</option>
            <option value={StoryLanguageTypeEnum.FRENCH}>French</option>
            <option value={StoryLanguageTypeEnum.GERMAN}>German</option>
          </select>
          {DropdownArrow()}
        </div>
      </div>
    </div>
  );

  // Self Written Story Creation Form
  const renderSelfWrittenForm = () => {
    // Get the current story configuration based on selected reading level
    const currentStoryConfig = storyconfig[selectedStoryConfig];

    const maxWordsPerPage = currentStoryConfig?.words_total ? parseInt(currentStoryConfig.words_total) : Infinity;
    const maxTextLength = currentStoryConfig?.max_text_length ? parseInt(currentStoryConfig.max_text_length) : Infinity;

    // Helper function to count words in text
    const countWords = (text: string) => {
      return text
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length;
    };

    // Helper function to validate text length
    const validateTextLength = (text: string) => {
      return text.length <= maxTextLength;
    };

    return (
      <div className="space-y-6">
        <div>
          <label className="text-left block text-base font-medium leading-6 text-secondaryStoryBlackText mb-3 ml-4">
            Page by page story breakdown
          </label>
          <div className="space-y-4 p-4 pb-0 border border-secondaryBorderColor rounded-lg bg-white">
            {Object.keys(pagesDataText).map((pageName, indexItem) => {
              const index = pagesDataText[pageName]?.index ? (pagesDataText[pageName]?.index || 0) - 1 : indexItem;
              const pageText = pagesDataText[pageName]?.text || '';
              const wordCount = countWords(pageText);
              const isWordCountValid = wordCount <= maxWordsPerPage;
              const isTextLengthValid = validateTextLength(pageText);
              const hasError = !isWordCountValid || !isTextLengthValid;

              return (
                <div>
                  <div key={pageName || index + 1} className="relative pt-6">
                    <label className="text-left block absolute left-2 top-0 text-sm font-medium leading-6 text-secondaryStoryBlackText mb-2">
                      Page {index + 1}
                    </label>
                    <textarea
                      className={`w-full rounded-md text-sm md:text-xl ${
                        indexItem !== Object.keys(pagesDataText)?.length - 1 ? 'border-b' : ''
                      }  px-2 pb-2 focus:outline-none  ${
                        hasError || (validation?.data?.[pageName] && !validation?.data?.[pageName].is_appropriate)
                          ? 'border-storyerror focus:border-storyerror'
                          : 'border-secondaryBorderColor focus:border-secondaryStoryAdditionalLightBlueText'
                      }`}
                      rows={3}
                      placeholder={index === 0 ? 'One sunny morning....' : ''}
                      value={pageText}
                      onChange={(e) => setPagesDataText(pageName, e.target.value)}
                      onBlur={onBlurAutosave}
                    />
                    <div className="absolute bottom-2 right-2 text-xs">
                      {!isWordCountValid ? (
                        <span className="text-secondaryStoryOrangeText text-xs mr-2">Word limit exceeded</span>
                      ) : !isTextLengthValid ? (
                        <span className="text-secondaryStoryOrangeText text-xs mr-2">Character limit exceeded</span>
                      ) : (
                        ''
                      )}
                      <span
                        className={
                          isWordCountValid && isTextLengthValid
                            ? 'text-secondaryDescriptionTextColor'
                            : 'text-secondaryStoryOrangeText'
                        }
                      >
                        {wordCount}/{maxWordsPerPage} Words
                      </span>
                    </div>
                  </div>
                  {validation?.data?.[pageName] && !validation?.data?.[pageName].is_appropriate && (
                    <div className="pl-2 text-xs flex gap-2 items-start justify-start text-left">
                      <span className={'text-secondaryStoryOrangeText'}>
                        {validation?.data?.[pageName].issue_found || 'Inappropriate content detected'}
                      </span>
                    </div>
                  )}
                  {validation?.data?.[pageName] &&
                    !validation?.data?.[pageName].is_appropriate &&
                    validation?.data?.[pageName].suggested_change && (
                      <div className="pl-2 text-xs hidden sm:inline-flex items-baseline justify-start gap-2 min-w-0 text-ellipsis overflow-hidden w-full">
                        <span className="text-secondaryStoryGrayButtonText font-bold shrink-0">Suggestion:</span>
                        <span className="text-secondaryStoryGrayButtonText flex-1 min-w-0 truncate text-left">
                          {validation?.data?.[pageName].suggested_change?.replace('Perhaps you meant:', ' ')}
                        </span>
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className=" w-full flex justify-between ">
        <div className="flex items-center align-end  mb-4">
          <h2 className="text-left text-2xl md:text-4xl font-bold text-storyblue ">Lets Build Your Story!</h2>
        </div>
      </div>

      <p className="mb-8 text-left text-sm text-storyblue">
        Tell us a little about your character and what the story's about
      </p>

      {/* Story Creation Method Selector */}
      <div className="mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-8">
          {tabs.map((tab) => (
            <div
              key={tab.value}
              className={`relative py-2 px-4 border-2 rounded-xl cursor-pointer transition-all ${
                storyCreationMethod === tab.value
                  ? 'border-secondaryStoryAdditionalLightBlueText'
                  : 'border-secondaryBorderColor hover:border-gray-500'
              }`}
              onClick={() => {
                setStoryCreationMethod(tab.value);
                dispatch(setStoryData({ storyCreationMethod: tab.value }));
              }}
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-secondaryStoryAdditionalLightBlueText rounded-full flex items-center justify-center">
                  {tab.icon}
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-bold text-secondaryStoryBlackText">{tab.title}</h3>
                  <p className="text-sm text-secondaryStoryBlackText">{tab.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Conditional Content Based on Selected Tab */}
      {storyCreationMethod === StoryCreationMethodEnum.AI_ASSISTED && renderAIAssistedForm()}
      {storyCreationMethod === StoryCreationMethodEnum.SELF_WRITTEN && renderSelfWrittenForm()}

      <div className={`flex flex-wrap ${onBack ? 'justify-between' : 'justify-end'} gap-2 md:flex-nowrap mt-5`}>
        {onBack && <BackButton id="step-back-button" onClick={onBack}></BackButton>}
        <button
          id="step-next-button"
          type="button"
          className={`px-6 py-2 h-9 font-bold leading-none rounded-2xl transition-all flex items-center bg-secondaryStoryAdditionalLightBlueText text-whiteText hover:shadow-md ${
            storyCreationMethod === StoryCreationMethodEnum.SELF_WRITTEN
              ? !validateSelfWrittenForm()
              : !validateAIWrittenForm()
                ? 'opacity-50 cursor-not-allowed'
                : ''
          }`}
          onClick={(event) => handleContinue(event as unknown as React.FormEvent<HTMLFormElement>)}
          disabled={
            submitLoader || storyCreationMethod === StoryCreationMethodEnum.SELF_WRITTEN
              ? !validateSelfWrittenForm()
              : !validateAIWrittenForm()
          }
        >
          Continue
          <Loader loader={submitLoader} />
        </button>
      </div>
    </>
  );
};

export default StepStoryDetailsInformation;
